/* game-room-frontend/src/pages/Caisse.jsx */
import React, { useCallback, useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "../components/AdminNavbar";

const Caisse = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [montant, setMontant] = useState("");
  const [motif, setMotif] = useState("");
  const [type, setType] = useState("depense");

  const [totalSessions, setTotalSessions] = useState(0);
  const [totalDepenses, setTotalDepenses] = useState(0);

  useEffect(() => {
    if (!user || !["admin", "gerant"].includes(user.role)) navigate("/dashboard");
  }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions/stats`);
      const data = await res.json();
      setTotalSessions(parseFloat(data.totalRevenue || 0));
    } catch (err) {
      console.error("Erreur récupération stats sessions :", err);
    }
  }, []);

  const fetchCaisse = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/caisse`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setEntries(data.entries || []);

      const totalD = data.entries
        .filter((e) => e.type === "depense" || e.type === "retrait")
        .reduce((sum, e) => sum + parseFloat(e.montant), 0);

      setTotalDepenses(totalD);
    } catch (err) {
      console.error("Erreur récupération caisse :", err);
    }
  }, [token]);

  useEffect(() => {
    fetchCaisse();
    fetchStats();
  }, [fetchCaisse, fetchStats]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!montant || !motif) return alert("Tous les champs sont requis");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/caisse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type, montant, motif }),
      });
      const data = await res.json();
      if (res.ok) {
        setMontant("");
        setMotif("");
        fetchCaisse();
      } else {
        alert(data.error || "Erreur ajout");
      }
    } catch (err) {
      alert("Erreur serveur");
    }
  };

  const totalRestant = totalSessions - totalDepenses;

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white px-4 py-10">
      <AdminNavbar />
      <h1 className="text-center text-3xl sm:text-4xl font-bold text-blue-400 mb-6">
        💰 Gestion de la Caisse
      </h1>

      {/* 🔘 Bouton Dashboard visible seulement pour le gérant */}
      {user?.role === "gerant" && (
        <div className="text-center mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded font-bold"
          >
            Retour au Dashboard
          </button>
        </div>
      )}

      <div className="max-w-2xl mx-auto bg-[#1a1a1a] rounded-xl p-6 shadow-lg mb-10">
        <h2 className="text-xl font-semibold mb-4">Ajouter une opération</h2>
        <form onSubmit={handleAdd} className="grid gap-4">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="p-2 rounded bg-[#2c2c2c] text-white"
          >
            <option value="depense">🧾 Dépense</option>
            <option value="retrait">🏧 Retrait</option>
          </select>
          <input
            type="number"
            placeholder="Montant (DT)"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            className="p-2 rounded bg-[#2c2c2c] text-white"
          />
          <input
            type="text"
            placeholder="Motif (ex: Loyer, produit nettoyage...)"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            className="p-2 rounded bg-[#2c2c2c] text-white"
          />
          <button className="bg-green-500 hover:bg-green-600 text-white p-2 rounded font-bold">
            Ajouter
          </button>
        </form>
      </div>

      <div className="max-w-4xl mx-auto grid gap-4 mb-8">
        <div className="text-xl text-center">
          <p className="text-green-400">
            💸 Total des recettes (sessions) :{" "}
            <span className="text-white">{totalSessions.toFixed(2)} DT</span>
          </p>
          <p className="text-yellow-400">
            🧾 Total des dépenses :{" "}
            <span className="text-white">{totalDepenses.toFixed(2)} DT</span>
          </p>
          <p className="text-blue-400 text-2xl font-bold mt-2">
            💼 Total restant : <span className="text-white">{totalRestant.toFixed(2)} DT</span>
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold text-center mb-4 text-green-400">
          🧾 Historique des opérations
        </h2>

        {entries.length === 0 ? (
          <p className="text-center text-gray-400">Aucune opération enregistrée.</p>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry, i) => (
              <div
                key={i}
                className={`p-4 rounded-lg shadow-md ${
                  entry.type === "depense"
                    ? "bg-[#2c2c2c] border-l-4 border-yellow-500"
                    : "bg-[#1f1f1f] border-l-4 border-red-500"
                }`}
              >
                <div className="flex justify-between">
                  <span className="font-semibold">{entry.motif}</span>
                  <span
                    className={`${
                      entry.type === "depense" ? "text-yellow-400" : "text-red-400"
                    }`}
                  >
                    - {parseFloat(entry.montant).toFixed(2)} DT
                  </span>
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  {new Date(entry.createdAt).toLocaleString("fr-FR")}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Caisse;

